import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';

import type { FacetGroup } from '@src/facets';
import ConditionGroupCard from '@src/components/catalog/CatalogRules/ConditionGroupCard';
import ConditionRow from '@src/components/catalog/CatalogRules/ConditionRow';
import { isConditionGroup } from '@src/components/catalog/CatalogRules/types';
import type { ConditionItem } from '@src/components/catalog/CatalogRules/types';
import { useConditionList } from '@src/components/catalog/CatalogRules/useConditionList';

interface ConditionBuilderProps {
  conditions: ConditionItem[];
  facetGroups: FacetGroup[];
  onChange: (conditions: ConditionItem[]) => void;
}

/**
 * Condition-only variant of the Catalog Rules "when" builder (reuses
 * ConditionRow/ConditionGroupCard directly — they're already pure, props-driven,
 * and carry no action-row or save/preview coupling), rendering just the root-level
 * condition list without Catalog Rules' own RuleCard chrome (actions, enable toggle, etc).
 */
const ConditionBuilder = ({ conditions, facetGroups, onChange }: ConditionBuilderProps) => {
  const {
    handleItemChange,
    handleItemDelete,
    handleTopLevelOperatorChange,
    handleAddRule,
    handleAddGroup,
  } = useConditionList(conditions, onChange);

  return (
    <Box>
      {conditions.map((item, i) => (
        isConditionGroup(item) ? (
          <ConditionGroupCard
            key={item.id}
            group={item}
            isFirst={i === 0}
            depth={0}
            facetGroups={facetGroups}
            onChange={handleItemChange}
            onDelete={handleItemDelete}
            onSiblingOperatorChange={handleTopLevelOperatorChange}
          />
        ) : (
          <ConditionRow
            key={item.id}
            condition={item}
            isFirst={i === 0}
            facetGroups={facetGroups}
            onChange={handleItemChange}
            onDelete={handleItemDelete}
            onSiblingOperatorChange={handleTopLevelOperatorChange}
          />
        )
      ))}

      <Box display="flex" gap={1} mt={1}>
        <Button size="small" startIcon={<AddIcon />} onClick={handleAddRule} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Add Rule
        </Button>
        <Button size="small" startIcon={<AddIcon />} onClick={handleAddGroup} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Add Rule Group
        </Button>
      </Box>
    </Box>
  );
};

export default ConditionBuilder;
